import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Col, Row, Modal, ModalHeader, ModalBody, Alert, Button } from "reactstrap";
import { toast } from "react-toastify";
import ComplianceScreeningService from "src/modules/3rd-party-management/apis/admin/compliance/screening/ScreeningService";
import AIRiskAssessmentModal from "src/modules/3rd-party-management/components/RisksAndTasks/risks/ai/AssessmentModal";
import AIGetHelpIcon from "src/modules/3rd-party-management/assets/images/microchip/white.png";
import AIBotty from "src/modules/3rd-party-management/assets/images/bottyAi-2.png";
import {
    CHAT_BOT_DOMAIN,
    API_BASE_URL
} from 'src/modules/3rd-party-management/constants';
import { RiInformationLine, RiThumbDownLine, RiThumbUpLine } from "react-icons/ri";
import RiskAccrodions from 'src/modules/3rd-party-management/pages/admin/compliance/components/profile-detail/RiskAccrodions';
import GeneralInfosTable from "src/modules/3rd-party-management/pages/admin/compliance/components/profile-detail/GeneralInfosTable";
import DeletionStatus from "src/modules/3rd-party-management/pages/admin/compliance/components/profile-detail/DeletionStatus";
import './styles.scss';

const ScreeningBody = ({ t, resultDetailQuery, setAiBasedRiskAssessmentModalStatus }) => {
    const {
        data: resultData
    } = resultDetailQuery;

    return (
        <Row className="d-flex flex-lg-row-reverse">
            <Col sm="12" md="12" lg="4" xl="3" className="d-none d-lg-block">
                <Row>
                    <Col sm="6" md="6" lg="12">
                        <div className="side-box ai-help-box mb-4">
                            <h5>
                                { t('Risk Assessment Support by AI') }
                            </h5>

                            <div className="image-box">
                                <img src={AIBotty} alt="Risk Assessment Support by AI" />
                            </div>

                            <Button onClick={() => {
                                setAiBasedRiskAssessmentModalStatus(true)
                            }} className="d-flex justify-content-center w-100" color="primary">
                                <img className="me-2" src={AIGetHelpIcon} alt="" />
                                { t('Get Help From AI') }
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Col>

            <Col sm="12" md="12" lg="8" xl="9">
                <div className="finding-content">
                    <DeletionStatus 
                        t={t}
                        isDeleted={resultData?.isDeleted}
                        deletionReason={resultData?.deletionReason}
                    />

                    <div className="alert alert-info d-flex justify-content-start align-items-center gap-2">
                        <RiInformationLine size="32" />
                        <div>
                            <b>{t('Please verify results')}</b>
                            <p className="mb-0">
                                {t('Results may be affected by factors such as similar names.')}<br/>
                                {t('Please review the output and provide feedback on its relevance:')}<br/>
                                <RiThumbUpLine size="20" /> {t('Thumbs Up: Relevant')} <br/>
                                <RiThumbDownLine size="20" /> {t('Thumbs Down: False Positive')}
                            </p>
                        </div>
                    </div>

                    <GeneralInfosTable 
                        resultData={resultData}
                        complianceType="screening"
                    />

                    <RiskAccrodions
                        data={resultData}
                    />
                </div>
            </Col>
        </Row>
    )
}

const ResultBody = function (props) {
    const {
        organization,
        t,
        supplierId,
        id,
        type,
        onItemNameChange
    } = props;

    const history = useHistory();
   
    const [aiBasedRiskAssessmentModalStatus, setAiBasedRiskAssessmentModalStatus] = useState(false);

    const resultDetailQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-screening-result-details',
            type,
            supplierId,
            id
        ],
        queryFn: async () => {
            const service = ComplianceScreeningService.getInstance();

            if (type?.toLowerCase() === 'business') {
                return await service.fetchBusinessScreeningResultItemDetails(
                    id
                );
            }

            return await service.fetchPersonScreeningResultItemDetails(
                id
            );
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load profile details. Please try again.'), {
                type: 'error'
            });
        },
    });

    useEffect(() => {
        if (!onItemNameChange) {
            return;
        }

        const resultData = resultDetailQuery.data;
        if (!resultData) {
            onItemNameChange('...');
            return;
        }

        const resultType = resultData?.type?.toLowerCase();
        const name = resultType === 'business'
            ? resultData?.name
            : [resultData?.firstName, resultData?.middleName, resultData?.lastName]
                .filter(Boolean)
                .join(' ');

        onItemNameChange(name || '...');
    }, [ resultDetailQuery.data, onItemNameChange ]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const renderLoadingPlaceholder = () => {
        return (
            <Row>
                <Col sm="12" md="9">
                    <div className="dt-field dt-skeleton mb-4" style={{ width: '70%' }}></div>
                    <div className="dt-field dt-skeleton mb-5" style={{ width: '50%', height: '220px' }}></div>

                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '85%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-4" style={{ width: '50%' }}></div>

                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '85%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-4" style={{ width: '50%' }}></div>

                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '85%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '50%' }}></div>
                </Col>

                <Col sm="12" md="3">
                    <div className="dt-field dt-skeleton w-100 mb-4" style={{ height: '220px' }}></div>
                    <div className="dt-field dt-skeleton w-100 mb-4" style={{ height: '220px' }}></div>
                    <div className="dt-field dt-skeleton w-100" style={{ height: '220px' }}></div>
                </Col>
            </Row>
        );
    }

    const renderModals = () => {
        const {
            datasets,
            pepEntries
        } = resultDetailQuery.data || {};

        const dataSetsToSend = [
            ...(datasets || [])
        ];

        if (pepEntries?.pepTier) {
            dataSetsToSend.push(pepEntries.pepTier);
        }

        const creationPayload = {
            [type?.toLowerCase() === 'business' ? 'screeningBusiness' : 'screeningPerson']: id,
            supplier: supplierId
        };

        return (
            aiBasedRiskAssessmentModalStatus && (
                <AIRiskAssessmentModal
                    closeModal={() => setAiBasedRiskAssessmentModalStatus(false)}
                    onRisksCreated={() => history.push({ search: '?tab=risk-assessment' })}
                    isOpen={aiBasedRiskAssessmentModalStatus}
                    translateResult={true}
                    aiTranlateRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/tpdd/risks-associated-translate.ai`,
                        payload: {
                            module: "TPDD_SCREENING_PERSONS",
                            datasets: dataSetsToSend,
                            organizationId: organization.id
                        }
                    }}
                    additionnalDetailsRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/tpdd/risk-ai-findings.get`,
                        payload: {}
                    }}
                    createRisksRequestOptions={{
                        requestUrl: `${API_BASE_URL}/risk_rate/create_ai`,
                        payload: creationPayload
                    }}>
                    {(result) => {
                        return (
                            <Modal className="ai-risk-result-modal mw-100" style={{ width: '90%' }} isOpen={aiBasedRiskAssessmentModalStatus} size="xl">
                                <ModalHeader toggle={() => setAiBasedRiskAssessmentModalStatus(!aiBasedRiskAssessmentModalStatus)}>
                                    {t('Risk Assesment Support by AI')}
                                </ModalHeader>
                                <ModalBody>
                                    <result.type {...result.props} />
                                </ModalBody>
                            </Modal>
                        )
                    }}
                </AIRiskAssessmentModal>
            )
        )
    }

    const renderContent = () => {
        if (resultDetailQuery.isFetching) {
            return renderLoadingPlaceholder();
        }

        if (resultDetailQuery.isError) {
            return (
                <Alert 
                    color="warning" 
                    className="my-3"
                >
                    <strong>{t('Unable to fetch compliance screening result.')}</strong>{' '}
                    {t('The item may not exist, or an unexpected error occurred. Please try again.')}
                </Alert>
            )
        }

        return (
            <>
                <ScreeningBody
                    setAiBasedRiskAssessmentModalStatus={setAiBasedRiskAssessmentModalStatus}
                    resultDetailQuery={resultDetailQuery}
                    t={t}
                />

                {renderModals()}
            </>
        )
    }

    return (
        <div className="finding-details-content">
            {renderContent()}
        </div>
    )
};

const mapStatetoProps = (state) => {
    const { Organization } = state;

    return {
        organization: Organization
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps,
        {}
    )(ResultBody)
);